/**
 * React Query hooks for QMS Trend Analysis
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useSession } from '@/hooks/useSession';
import {
  fetchQmsTrends,
  fetchQmsTrendHistory,
  fetchQmsTrendPatterns,
  fetchQmsTrendSummary,
} from '../api/qmsApi';
import { getQmsOrganisationId } from '../utils/organisation';
import type {
  QmsTrendAnalysis,
  QmsTrendHistory,
  QmsTrendPatterns,
  QmsTrendSummaryResponse,
  QmsPatternSeverity,
  QmsPatternType,
} from '../types/qms.types';

/**
 * Query keys for trend analysis
 */
export const qmsTrendKeys = {
  all: ['qms', 'trends'] as const,
  latest: (organisation: string) => [...qmsTrendKeys.all, 'latest', organisation] as const,
  history: (organisation: string, days: number) =>
    [...qmsTrendKeys.all, 'history', organisation, days] as const,
  patterns: (organisation: string, severity?: QmsPatternSeverity[], patternType?: QmsPatternType[]) =>
    [...qmsTrendKeys.all, 'patterns', organisation, severity, patternType] as const,
  summary: (organisation: string, days: number) =>
    [...qmsTrendKeys.all, 'summary', organisation, days] as const,
};

/**
 * Hook to fetch latest trend analysis for the current organisation
 */
export function useQmsTrends(
  options?: Omit<UseQueryOptions<QmsTrendAnalysis>, 'queryKey' | 'queryFn'>
) {
  const { user } = useSession();
  const organisation = getQmsOrganisationId(user?.organisation);

  return useQuery<QmsTrendAnalysis>({
    queryKey: qmsTrendKeys.latest(organisation),
    queryFn: () => fetchQmsTrends(organisation),
    enabled: !!organisation,
    staleTime: 5 * 60 * 1000, // 5 minutes - trends update daily
    ...options,
  });
}

/**
 * Hook to fetch historical trend data
 */
export function useQmsTrendHistory(
  days: number = 30,
  options?: Omit<UseQueryOptions<QmsTrendHistory>, 'queryKey' | 'queryFn'>
) {
  const { user } = useSession();
  const organisation = getQmsOrganisationId(user?.organisation);

  return useQuery<QmsTrendHistory>({
    queryKey: qmsTrendKeys.history(organisation, days),
    queryFn: () => fetchQmsTrendHistory(organisation, days),
    enabled: !!organisation,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch patterns with optional filtering
 */
export function useQmsTrendPatterns(
  filterOptions?: {
    severity?: QmsPatternSeverity[];
    pattern_type?: QmsPatternType[];
  },
  queryOptions?: Omit<UseQueryOptions<QmsTrendPatterns>, 'queryKey' | 'queryFn'>
) {
  const { user } = useSession();
  const organisation = getQmsOrganisationId(user?.organisation);

  return useQuery<QmsTrendPatterns>({
    queryKey: qmsTrendKeys.patterns(
      organisation,
      filterOptions?.severity,
      filterOptions?.pattern_type
    ),
    queryFn: () => fetchQmsTrendPatterns(organisation, filterOptions),
    enabled: !!organisation,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...queryOptions,
  });
}

/**
 * Hook to fetch aggregated trend summary
 */
export function useQmsTrendSummary(
  days: number = 30,
  options?: Omit<UseQueryOptions<QmsTrendSummaryResponse>, 'queryKey' | 'queryFn'>
) {
  const { user } = useSession();
  const organisation = getQmsOrganisationId(user?.organisation);

  return useQuery<QmsTrendSummaryResponse>({
    queryKey: qmsTrendKeys.summary(organisation, days),
    queryFn: () => fetchQmsTrendSummary(organisation, days),
    enabled: !!organisation,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to check for critical/high severity patterns (for alerts)
 */
export function useQmsCriticalPatterns() {
  const { data: trends, ...queryInfo } = useQmsTrends();

  const criticalCount = trends?.summary?.critical || 0;
  const highCount = trends?.summary?.high || 0;
  const hasCriticalAlerts = criticalCount > 0 || highCount > 0;

  return {
    criticalCount,
    highCount,
    hasCriticalAlerts,
    totalCriticalAndHigh: criticalCount + highCount,
    ...queryInfo,
  };
}
